import { Avatar, Flex, Text } from '@highlight-cp/core-ui';
import type { AvatarProps } from '@highlight-cp/core-ui';

/** How many names Facepile shows before the `+N` label, when `maxVisible` is omitted. */
export const FACEPILE_DEFAULT_MAX_VISIBLE = 5;
const DEFAULT_AVATAR_SIZE = 'md' satisfies NonNullable<AvatarProps['size']>;
// Avatar.Group's default overlap is the `sm` spacing (~12px), which was sized for the white ring
// it draws around each avatar. The tinted avatars carry their ring inside their own box, so the
// overlap is what's visible: 5px at `md` per the Figma facepile, scaled down for the small sizes
// (a 12px overlap on a 16px `xs` avatar would leave a sliver) and up for the large ones.
const GROUP_SPACING: Record<string, number> = { xs: 4, sm: 4, md: 5, lg: 8, xl: 12 };
// The Figma facepile's palette. Mantine's default set also reaches for cyan, grape, lime and the
// like, which have no counterpart in the brand scales.
const INITIALS_COLORS = ['green', 'orange', 'blue', 'gray', 'yellow'];

/**
 * Names to show as initials avatars, in display order.
 */
export interface FacepileProps {
  /**
   * Names, in display order. Initials and color come from each name.
   * Names-only on purpose: an image facepile would be a new item shape, not a second component.
   */
  names: string[];
  /**
   * How many names to show before the `+N` label.
   * @default {@link FACEPILE_DEFAULT_MAX_VISIBLE}
   */
  maxVisible?: number;
  /**
   * Size of each avatar, including the overflow count's line height. Matches core-ui `Avatar`.
   * @default 'md'
   */
  size?: AvatarProps['size'];
  /**
   * Accessible name for the group. Omit it and the group stays unlabeled;
   * each avatar's accessible name is that person's name.
   */
  label?: string;
  /**
   * Accessible name for the overflow count. Overrides the generated English
   * `${overflow} more` label. Pass it when that count needs a translation;
   * this composition does not load one itself.
   */
  overflowLabel?: string;
}

/**
 * ### Facepile
 *
 * @description
 * Overlapping initials avatars from a list of names, laid out with core-ui
 * `Avatar.Group`. Shows `maxVisible` names (default 5) at `size` (default `'md'`).
 * The rest are a plain `+N` label beside the avatars (no circle), named `${overflow} more` unless
 * `overflowLabel` is set. Blank names are dropped before that count. An empty
 * list renders nothing. Pass `label` when the group itself needs an accessible name.
 * Avatars are `tinted` (a -2 fill in a -3 ring, per the Figma facepile), and the overlap scales
 * with `size` so the small circles stay readable.
 *
 * @example
 * <Facepile names={['Ada Lovelace', 'Grace Hopper']} />
 * <Facepile names={names} label="Custom audience" overflowLabel="15 more people" />
 *
 * @param props - {@link FacepileProps}
 * @returns React component
 */
export const Facepile = ({
  names,
  maxVisible = FACEPILE_DEFAULT_MAX_VISIBLE,
  size = DEFAULT_AVATAR_SIZE,
  label,
  overflowLabel,
}: FacepileProps) => {
  const normalizedNames = names.map((name) => name.trim()).filter(Boolean);
  if (normalizedNames.length === 0) {
    return null;
  }

  // slice() treats a negative end as an offset from the end, which would show the wrong names.
  const visibleCount = Number.isFinite(maxVisible) ? Math.max(0, Math.trunc(maxVisible)) : 0;
  const visibleNames = normalizedNames.slice(0, visibleCount);
  const overflow = normalizedNames.length - visibleNames.length;
  const overflowName = overflowLabel?.trim() || `${overflow} more`;

  return (
    <Flex align="center" gap="xs">
      <Avatar.Group
        aria-label={label}
        role={label ? 'group' : undefined}
        spacing={typeof size === 'string' ? GROUP_SPACING[size] : undefined}
      >
        {visibleNames.map((name, index) => (
          <Avatar
            allowedInitialsColors={INITIALS_COLORS}
            alt={name}
            aria-label={name}
            color="initials"
            key={`${name}-${index}`}
            name={name}
            role="img"
            size={size}
            variant="tinted"
          />
        ))}
      </Avatar.Group>
      {overflow > 0 && (
        // Plain text, not an avatar circle: no fill or border, set off from the bubbles by the gap.
        <Text aria-label={overflowName} c="dimmed" component="span" fw={600} role="img" size="xs">
          +{overflow}
        </Text>
      )}
    </Flex>
  );
};
